import { useCallback, useEffect, useState } from 'react';

/**
 * 같은 탭 내에서 useLocalStorage 인스턴스들이 서로의 갱신을 감지하기 위한 커스텀 이벤트 이름.
 *
 * 표준 `storage` 이벤트는 **다른 탭/창에서의 변경에만** 발생하므로 같은 탭 내
 * 다중 인스턴스는 동기화되지 않는다. 본 훅은 갱신/삭제 시 이 커스텀 이벤트를
 * 발행하여 같은 탭의 다른 인스턴스들이 즉시 재읽기하도록 보장한다.
 */
const SAME_TAB_SYNC_EVENT = 'darak-kidday/local-storage-sync';

/**
 * 커스텀 이벤트의 페이로드 — 어떤 키가 변경되었는지를 알려준다.
 * 값 자체는 싣지 않고, 수신 측이 localStorage에서 다시 읽도록 한다 (직렬화된 단일 진실).
 */
interface SameTabSyncDetail {
  key: string;
}

/** 커스텀 이벤트 발행 — 같은 탭 내 다른 인스턴스에게 키 변경을 알림. */
function emitSameTabSync(key: string): void {
  if (typeof window === 'undefined') {
    return;
  }
  const detail: SameTabSyncDetail = { key };
  window.dispatchEvent(new CustomEvent<SameTabSyncDetail>(SAME_TAB_SYNC_EVENT, { detail }));
}

/**
 * localStorage와 동기화되는 React 상태 훅.
 *
 * 다른 탭/창에서의 변경은 표준 `storage` 이벤트로,
 * 같은 탭 내 다른 인스턴스의 변경은 커스텀 이벤트(`SAME_TAB_SYNC_EVENT`)로 반영된다.
 * SSR 환경에서도 깨지지 않도록 `typeof window` 체크를 거친다.
 *
 * @param key     localStorage 키
 * @param initial 값이 없을 때 사용할 초기값
 */
export function useLocalStorage<T>(
  key: string,
  initial: T,
): readonly [T, (next: T | ((prev: T) => T)) => void, () => void] {
  const readValue = useCallback((): T => {
    if (typeof window === 'undefined') {
      return initial;
    }
    try {
      const raw = window.localStorage.getItem(key);
      if (raw === null) {
        return initial;
      }
      return JSON.parse(raw) as T;
    } catch {
      return initial;
    }
  }, [key, initial]);

  const [value, setValue] = useState<T>(readValue);

  const update = useCallback(
    (next: T | ((prev: T) => T)) => {
      setValue((prev) => {
        const resolved =
          typeof next === 'function' ? (next as (p: T) => T)(prev) : next;
        try {
          window.localStorage.setItem(key, JSON.stringify(resolved));
        } catch {
          // 사용 불가 환경(시크릿 모드 등) — 메모리 상태만 유지
        }
        // 같은 탭 내 다른 인스턴스에게 변경을 알림 (표준 storage 이벤트는 같은 탭에선 발생하지 않음).
        emitSameTabSync(key);
        return resolved;
      });
    },
    [key],
  );

  const remove = useCallback(() => {
    try {
      window.localStorage.removeItem(key);
    } catch {
      // ignore
    }
    setValue(initial);
    emitSameTabSync(key);
  }, [key, initial]);

  // 다른 탭 → storage 이벤트 / 같은 탭 → 커스텀 이벤트 모두 처리.
  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    const onStorage = (e: StorageEvent): void => {
      if (e.key !== key) {
        return;
      }
      setValue(readValue());
    };
    const onSameTabSync = (e: Event): void => {
      const detail = (e as CustomEvent<SameTabSyncDetail>).detail;
      if (detail.key !== key) {
        return;
      }
      setValue(readValue());
    };
    window.addEventListener('storage', onStorage);
    window.addEventListener(SAME_TAB_SYNC_EVENT, onSameTabSync);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener(SAME_TAB_SYNC_EVENT, onSameTabSync);
    };
  }, [key, readValue]);

  return [value, update, remove] as const;
}
